// Is this email allowed into the CTE Dashboard?
//
// "listed" when this project's `provision` collection lists the email or its
// domain — the same lookup useSignup has always made, so the sign-up list and
// the sign-in list are one list. Both login (useLogin) and session restore
// (AuthContext) consult it, because the Framework Builder creates accounts in
// this same Auth project from a broader provisioning list
// (numetrics-ctelink-provision): an account that exists here is not, by
// itself, an account that belongs here.
//
// No `active` filter, on purpose: docs in this store carry no such field and
// useSignup never read one. To revoke here, DELETE the doc (deactivating is
// the other store's convention and would not remove the match).
//
// "unknown" means the lookup could not be trusted (request failed, or an
// empty answer served from the cold offline cache). Callers deny on it —
// fail closed — but can word the message as a retry rather than a refusal.
import { db } from "./config";
import { collection, getDocs, query, where } from "firebase/firestore";
import { isSsoProviderId } from "./sso";

// The one provision query: docs matching the exact email or its domain.
export const queryProvision = (email) => {
  const emailLower = email.toLowerCase();
  return getDocs(
    query(
      collection(db, "provision"),
      where("matchDomain", "in", [emailLower, emailLower.split("@")[1]])
    )
  );
};

// The district identity provider for this email, or null for password login.
// An exact-email doc governs over a domain doc, so one person can be kept on
// a password inside an SSO domain.
export const ssoProviderIdOf = (snap, email) => {
  const docs = snap.docs.map((d) => d.data());
  const match =
    docs.find((d) => d.matchDomain === email.toLowerCase()) || docs[0];
  return match && isSsoProviderId(match.ssoProviderId)
    ? match.ssoProviderId
    : null;
};

export const provisionLookup = async (email) => {
  try {
    if (!email) return { status: "unlisted", ssoProviderId: null };
    const snap = await queryProvision(email);
    if (!snap.empty) {
      return { status: "listed", ssoProviderId: ssoProviderIdOf(snap, email) };
    }
    return {
      status: snap.metadata.fromCache ? "unknown" : "unlisted",
      ssoProviderId: null,
    };
  } catch (err) {
    return { status: "unknown", ssoProviderId: null };
  }
};

export const provisionStatus = async (email) =>
  (await provisionLookup(email)).status;

export const isProvisioned = async (email) =>
  (await provisionStatus(email)) === "listed";
